import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';

const defaultDateFrom = format(subDays(new Date(), 30), 'dd.MM.yyyy');
const defaultDateTo = format(new Date(), 'dd.MM.yyyy');

const TAB_STATS = 'stats';
const TAB_REPORTS = 'reports';

async function downloadPdfBlob(url, params, fallbackFilename) {
  const response = await axios.get(url, {
    params,
    responseType: 'blob',
  });

  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json')) {
    const text = await response.data.text();
    let message = 'Ошибка генерации отчёта';
    try {
      message = JSON.parse(text).error || message;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }

  // Ошибка API может прийти как JSON в blob с другим content-type
  if (response.data.type === 'application/json') {
    const text = await response.data.text();
    let message = 'Ошибка генерации отчёта';
    try {
      message = JSON.parse(text).error || message;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', fallbackFilename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export default function Reports() {
  const [tab, setTab] = useState(TAB_STATS);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [appliedDateFrom, setAppliedDateFrom] = useState(defaultDateFrom);
  const [appliedDateTo, setAppliedDateTo] = useState(defaultDateTo);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Поля вкладки «Отчёты»
  const [salesPeriodFrom, setSalesPeriodFrom] = useState(defaultDateFrom);
  const [salesPeriodTo, setSalesPeriodTo] = useState(defaultDateTo);
  const [salesAuthorId, setSalesAuthorId] = useState('');
  const [salesAuthorFrom, setSalesAuthorFrom] = useState(defaultDateFrom);
  const [salesAuthorTo, setSalesAuthorTo] = useState(defaultDateTo);
  const [pdfLoading, setPdfLoading] = useState(null);

  const { user } = useAuth();
  const { data: stats, isLoading: loadingStats } = useQuery(
    ['statistics', user?.id, user?.allowed_collection_codes],
    async () => {
      const response = await axios.get('/api/reports/statistics');
      return response.data;
    }
  );

  const { data: movements, isLoading: loadingMovements, isError: movementsError, error: movementsErr } = useQuery(
    ['movements', appliedDateFrom, appliedDateTo],
    async () => {
      const response = await axios.get('/api/reports/movements', {
        params: { date_from: appliedDateFrom, date_to: appliedDateTo },
      });
      return response.data;
    }
  );

  const { data: authors } = useQuery(
    'authors',
    async () => {
      const response = await axios.get('/api/authors');
      return response.data;
    },
    { enabled: tab === TAB_REPORTS }
  );

  useEffect(() => {
    if (movementsError && movementsErr) {
      const msg = movementsErr.response?.data?.error ?? movementsErr.message ?? 'Ошибка загрузки движения фондов';
      setSnackbar((s) => ({ ...s, open: true, message: msg, severity: 'error' }));
    }
  }, [movementsError, movementsErr]);

  const handleUpdateMovements = () => {
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const [artworksRes, authorsRes] = await Promise.all([
        axios.get('/api/artworks', { params: { per_page: 100000 } }),
        axios.get('/api/authors', { params: { limit: 100000 } }),
      ]);
      const artworks = artworksRes.data?.artworks ?? [];
      const authorsList = authorsRes.data ?? [];

      const artworkRows = artworks.map((a) => ({
        'ID': a.id,
        'Инв. номер': a.inventory_number ?? '',
        'Дата регистрации': a.registration_date ?? '',
        'Код коллекции': a.collection_code ?? '',
        'ID автора': a.author_id ?? '',
        'Автор': a.author?.full_name ?? '',
        'Название': a.artwork_title ?? '',
        'Описание': a.artwork_description ?? '',
        'Материал и техника': a.material_and_technique ?? '',
        'Высота (см)': a.height_cm ?? '',
        'Ширина (см)': a.width_cm ?? '',
        'Глубина (см)': a.depth_cm ?? '',
        'Вес (кг)': a.weight_kg ?? '',
        'Состояние': a.condition ?? '',
        'Детали состояния': a.condition_details ?? '',
        'Источник поступления': a.source_of_acquisition ?? '',
        'Дата поступления': a.acquisition_date ?? '',
        'Номер договора': a.contract_number ?? '',
        'Дата договора': a.contract_date ?? '',
        'Стоимость покупки': a.agreed_price ?? '',
        'Валюта покупки': a.purchase_currency ?? 'RUB',
        'Место приобретения': a.acquisition_place ?? '',
        'Год создания': a.creation_year_display ?? a.creation_year ?? '',
        'Подписи и марки': a.signatures_and_marks ?? '',
        'Фото': a.photo_reference ?? '',
        'Выставочные заметки': a.exhibition_notes ?? '',
        'Статус': a.status ?? '',
        'Дата продажи': a.sales_date ?? '',
        'Фактическая стоимость продажи': a.sales_price ?? '',
        'Валюта продажи': a.sales_currency ?? 'RUB',
        'Покупатель': a.buyer_name ?? '',
        'Внутренние заметки': a.internal_notes ?? '',
        'Место создания': a.storage_place ?? '',
        'Купивший коллекционер': a.buying_collector ?? '',
        'Локация хранения': a.storage_location ?? '',
        'Создано': a.created_at ?? '',
        'Обновлено': a.updated_at ?? '',
        'Кем создано': a.created_by ?? '',
        'Кем обновлено': a.updated_by ?? '',
        'Теги': (a.tags || []).map((t) => t.name).join(', '),
      }));

      const authorRows = authorsList.map((a) => ({
        'ID': a.id,
        'ФИО': a.full_name ?? '',
        'Фамилия': a.last_name ?? '',
        'Год рождения': a.birth_year ?? '',
        'Год смерти': a.death_year ?? '',
        'Биография': a.bio ?? '',
        'Кол-во произведений': a.artworks_count ?? 0,
        'Создано': a.created_at ?? '',
        'Обновлено': a.updated_at ?? '',
      }));

      const wb = XLSX.utils.book_new();
      wb.Props = {
        Title: 'Экспорт данных',
        Subject: 'Произведения и авторы',
        Author: '',
        Company: '',
      };
      const wsArtworks = XLSX.utils.json_to_sheet(artworkRows);
      const wsAuthors = XLSX.utils.json_to_sheet(authorRows);
      XLSX.utils.book_append_sheet(wb, wsArtworks, 'Произведения');
      XLSX.utils.book_append_sheet(wb, wsAuthors, 'Авторы');

      const fileName = `export_data_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      showSnackbar(`Экспорт выполнен: ${fileName}`, 'success');
    } catch (err) {
      const msg = err.response?.data?.error ?? err.message ?? 'Ошибка при экспорте';
      showSnackbar(msg, 'severity');
    } finally {
      setExportLoading(false);
    }
  };

  const handlePdfError = async (err) => {
    let msg = err.message ?? 'Ошибка генерации PDF';
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        if (parsed.error) msg = parsed.error;
      } catch (_) {
        /* ignore */
      }
    } else if (err.response?.data?.error) {
      msg = err.response.data.error;
    }
    showSnackbar(msg, 'error');
  };

  const handleSalesByPeriodPdf = async () => {
    setPdfLoading('period');
    try {
      await downloadPdfBlob(
        '/api/reports/sales-by-period.pdf',
        { date_from: salesPeriodFrom, date_to: salesPeriodTo },
        'sales_by_period.pdf'
      );
      showSnackbar('Отчёт о продажах за период сформирован', 'success');
    } catch (err) {
      await handlePdfError(err);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleSalesByAuthorPdf = async () => {
    if (!salesAuthorId) {
      showSnackbar('Выберите автора', 'error');
      return;
    }
    setPdfLoading('author');
    try {
      await downloadPdfBlob(
        '/api/reports/sales-by-author.pdf',
        {
          author_id: salesAuthorId,
          date_from: salesAuthorFrom,
          date_to: salesAuthorTo,
        },
        'sales_by_author.pdf'
      );
      showSnackbar('Отчёт о продажах по автору сформирован', 'success');
    } catch (err) {
      await handlePdfError(err);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleCollectorsPdf = async () => {
    setPdfLoading('collectors');
    try {
      await downloadPdfBlob('/api/reports/collectors.pdf', {}, 'collectors.pdf');
      showSnackbar('Отчёт по коллекционерам сформирован', 'success');
    } catch (err) {
      await handlePdfError(err);
    } finally {
      setPdfLoading(null);
    }
  };

  const statsLoading = tab === TAB_STATS && (loadingStats || loadingMovements);

  if (statsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Отчёты и статистика
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Статистика" value={TAB_STATS} />
        <Tab label="Отчёты" value={TAB_REPORTS} />
      </Tabs>

      {tab === TAB_STATS && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Экспорт данных
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Сохранить всю информацию по произведениям и авторам в таблицу Excel.
              </Typography>
              <Button
                variant="contained"
                startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <TableChartIcon />}
                onClick={handleExportExcel}
                disabled={exportLoading}
              >
                {exportLoading ? 'Экспорт…' : 'Экспорт данных в Excel'}
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Статистика фондов
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Всего произведений
                  </Typography>
                  <Typography variant="h5">{stats?.total_artworks || 0}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    В экспозиции
                  </Typography>
                  <Typography variant="h5">
                    {stats?.statuses?.['в экспозиции'] || 0}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Стоимость покупки по валютам
              </Typography>
              <Grid container spacing={2}>
                {['RUB', 'USD', 'EUR', 'CNY'].map((code) => (
                  <Grid item xs={6} md={3} key={`purchase-${code}`}>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.purchase_by_currency?.[code]?.name || code}
                    </Typography>
                    <Typography variant="h6">
                      {new Intl.NumberFormat('ru-RU').format(
                        stats?.purchase_by_currency?.[code]?.total || 0
                      )}{' '}
                      {code}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Фактическая стоимость продажи по валютам
              </Typography>
              <Grid container spacing={2}>
                {['RUB', 'USD', 'EUR', 'CNY'].map((code) => (
                  <Grid item xs={6} md={3} key={`sales-${code}`}>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.sales_by_currency?.[code]?.name || code}
                    </Typography>
                    <Typography variant="h6">
                      {new Intl.NumberFormat('ru-RU').format(
                        stats?.sales_by_currency?.[code]?.total || 0
                      )}{' '}
                      {code}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Движение фондов
              </Typography>
              <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <TextField
                  label="Дата от"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="ДД.ММ.ГГГГ"
                  size="small"
                />
                <TextField
                  label="Дата до"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="ДД.ММ.ГГГГ"
                  size="small"
                />
                <Button variant="contained" onClick={handleUpdateMovements}>
                  Обновить
                </Button>
              </Box>

              {movements?.period && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Период: с {movements.period.from} по {movements.period.to}
                </Typography>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">
                    Поступило: {movements?.counts?.received ?? 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">
                    Продано: {movements?.counts?.sold ?? 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">
                    Зарегистрировано: {movements?.counts?.registered ?? 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">
                    В экспозиции: {movements?.counts?.in_exhibition ?? 0}
                  </Typography>
                </Grid>
              </Grid>

              {movements?.registered && movements.registered.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Зарегистрировано в системе за период
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Инв. номер</TableCell>
                          <TableCell>Название</TableCell>
                          <TableCell>Дата регистрации</TableCell>
                          <TableCell>Дата поступления</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movements.registered.slice(0, 20).map((artwork) => (
                          <TableRow key={artwork.id}>
                            <TableCell>{artwork.inventory_number}</TableCell>
                            <TableCell>{artwork.artwork_title}</TableCell>
                            <TableCell>{artwork.registration_date ?? '—'}</TableCell>
                            <TableCell>{artwork.acquisition_date ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {movements?.received && movements.received.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Поступившие произведения
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Инв. номер</TableCell>
                          <TableCell>Название</TableCell>
                          <TableCell>Дата поступления</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movements.received.slice(0, 20).map((artwork) => (
                          <TableRow key={artwork.id}>
                            <TableCell>{artwork.inventory_number}</TableCell>
                            <TableCell>{artwork.artwork_title}</TableCell>
                            <TableCell>{artwork.acquisition_date}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === TAB_REPORTS && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Отчёт о продажах за период
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Таблица: № / ФИО автора / Наименование произведений / Сумма продаж.
                Упорядочено по имени автора. Внизу — общая сумма продаж по валютам.
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  label="Дата от"
                  value={salesPeriodFrom}
                  onChange={(e) => setSalesPeriodFrom(e.target.value)}
                  placeholder="ДД.ММ.ГГГГ"
                  size="small"
                />
                <TextField
                  label="Дата до"
                  value={salesPeriodTo}
                  onChange={(e) => setSalesPeriodTo(e.target.value)}
                  placeholder="ДД.ММ.ГГГГ"
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={
                    pdfLoading === 'period'
                      ? <CircularProgress size={20} color="inherit" />
                      : <PictureAsPdfIcon />
                  }
                  onClick={handleSalesByPeriodPdf}
                  disabled={pdfLoading !== null}
                >
                  {pdfLoading === 'period' ? 'Формирование…' : 'Сформировать PDF'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Отчёт о продажах по автору
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Таблица: № / ФИО / Наименование / Сумма. Упорядочено по сумме (от большей к меньшей).
                Внизу — общая сумма продаж по валютам.
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="sales-author-label">Автор</InputLabel>
                  <Select
                    labelId="sales-author-label"
                    label="Автор"
                    value={salesAuthorId}
                    onChange={(e) => setSalesAuthorId(e.target.value)}
                  >
                    {(authors || []).map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.full_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Дата от"
                  value={salesAuthorFrom}
                  onChange={(e) => setSalesAuthorFrom(e.target.value)}
                  placeholder="ДД.ММ.ГГГГ"
                  size="small"
                />
                <TextField
                  label="Дата до"
                  value={salesAuthorTo}
                  onChange={(e) => setSalesAuthorTo(e.target.value)}
                  placeholder="ДД.ММ.ГГГГ"
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={
                    pdfLoading === 'author'
                      ? <CircularProgress size={20} color="inherit" />
                      : <PictureAsPdfIcon />
                  }
                  onClick={handleSalesByAuthorPdf}
                  disabled={pdfLoading !== null}
                >
                  {pdfLoading === 'author' ? 'Формирование…' : 'Сформировать PDF'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Отчёт по коллекционерам
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Таблица: № / ФИО коллекционера / Наименование. Упорядочено по именам коллекционеров.
              </Typography>
              <Button
                variant="contained"
                startIcon={
                  pdfLoading === 'collectors'
                    ? <CircularProgress size={20} color="inherit" />
                    : <PictureAsPdfIcon />
                }
                onClick={handleCollectorsPdf}
                disabled={pdfLoading !== null}
              >
                {pdfLoading === 'collectors' ? 'Формирование…' : 'Сформировать PDF'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
